import { useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const stats = [
  { value: '+10 Mil', label: 'Veículos Atendidos', icon: 'ri-car-fill' },
  { value: '+5 Mil', label: 'Clientes Satisfeitos', icon: 'ri-user-heart-fill' },
  { value: '+27 Mil', label: 'Serviços Executados', icon: 'ri-tools-fill' },
  { value: '11+', label: 'Anos de Experiência', icon: 'ri-award-fill' },
];

const supplies = [
  { name: 'Liqui-Moly', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/liqui-moly.png' },
  { name: 'Champion', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/champion.png' },
  { name: 'Castrol', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/castrak.png' },
  { name: 'Mobil', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/mobi.png' },
  { name: 'Febi', logo: 'https://www.mikimotors.com.br/wp-content/uploads/2024/01/febi.png' },
];

export default function About() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { ref: statsRef, visible: statsVisible } = useScrollReveal();
  const { ref: suppliesRef, visible: suppliesVisible } = useScrollReveal();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="sobre" className="relative py-24 overflow-hidden" ref={ref} style={{ background: 'linear-gradient(160deg, #161616 0%, #1c1c1c 30%, #181818 60%, #141414 100%)' }}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/15 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/10 to-transparent"></div>
        <div className="absolute -top-20 right-1/4 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(192,192,192,0.05) 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(180,160,120,0.04) 0%, transparent 70%)' }}></div>
        <div className="absolute top-1/2 left-0 w-px h-1/2 bg-gradient-to-b from-transparent via-[#C0C0C0]/10 to-transparent"></div>
      </div>
      <div className="max-w-7xl mx-auto px-6">
        {/* Split layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Left - Images */}
          <div
            className={`relative transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div className="relative rounded-3xl overflow-hidden h-[480px]">
              <img
                src="https://www.mikimotors.com.br/wp-content/uploads/2024/01/2023-07-17-4-2.jpg"
                alt="Oficina Miki Motors"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#C0C0C0] rounded-2xl p-5 shadow-2xl">
              <div className="text-black font-black text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                11+
              </div>
              <div className="text-black/70 text-sm font-semibold">Anos de Experiência</div>
            </div>
            <div className="absolute -top-6 -left-6 w-40 h-40 rounded-2xl overflow-hidden border-4 border-[#1a1a1a]">
              <img
                src="https://www.mikimotors.com.br/wp-content/uploads/2024/01/2023-07-17-3-2.jpg"
                alt="Detalhe oficina"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right - Content */}
          <div
            className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <span className="inline-block text-[#C0C0C0] text-sm font-semibold tracking-widest uppercase mb-4">
              Sobre Nós
            </span>
            <h2
              className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Cuidando do Seu Importado com{' '}
              <span className="text-[#D4D4D4]">Profissionalismo</span> e Precisão
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-6">
              A Miki Motors Imports é uma oficina mecânica especializada em automóveis importados, 
              localizada em Campinas – SP. Com mais de 11 anos de experiência, trabalhamos com as 
              melhores e maiores marcas do mercado, oferecendo um serviço de qualidade e rapidez.
            </p>
            <p className="text-white/60 text-base leading-relaxed mb-8">
              Contamos com profissionais qualificados para cada tipo de serviço, seja manutenção, 
              reparo ou upgrade. Nosso compromisso é garantir que seu veículo importado funcione 
              com máxima performance e segurança.
            </p>
            <div className="flex flex-col gap-4 mb-8">
              {[
                'Diagnóstico computadorizado de última geração',
                'Equipe certificada pelas principais marcas',
                'Peças originais e de primeira linha',
                'Garantia em todos os serviços realizados',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#C0C0C0]/20 flex-shrink-0">
                    <i className="ri-check-line text-[#C0C0C0] text-xs"></i>
                  </div>
                  <span className="text-white/70 text-sm">{item}</span>
                </div>
              ))}
            </div>
            <a
              href="https://api.whatsapp.com/send/?phone=5519997889998&text=Olá, gostaria de saber mais sobre a Miki Motors&type=phone_number&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#C0C0C0] text-black font-bold px-8 py-4 rounded-full hover:bg-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Fale com a Gente
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>

        {/* Stats */}
        <div ref={statsRef as React.RefObject<HTMLDivElement>} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`bg-[#111111] border border-white/5 rounded-2xl p-6 text-center transition-all duration-700 hover:border-[#C0C0C0]/30 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: statsVisible ? `${i * 100}ms` : '0ms' }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C0C0C0]/10 mx-auto mb-4">
                <i className={`${stat.icon} text-[#C0C0C0] text-xl`}></i>
              </div>
              <div className="text-3xl font-black text-[#D4D4D4] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                {stat.value}
              </div>
              <div className="text-white/50 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Supplies */}
        <div
          ref={suppliesRef as React.RefObject<HTMLDivElement>}
          className={`transition-all duration-700 ${suppliesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="text-center mb-10">
            <span className="text-white/40 text-sm tracking-widest uppercase">Suprimentos que trabalhamos</span>
          </div>

          {/* Infinite scroll strip */}
          <div className="relative overflow-hidden">
            {/* Left fade */}
            <div className="absolute left-0 top-0 h-full w-24 z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to right, #161616 0%, transparent 100%)' }}></div>
            {/* Right fade */}
            <div className="absolute right-0 top-0 h-full w-24 z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to left, #161616 0%, transparent 100%)' }}></div>

            <div
              className="flex items-center gap-8"
              style={{
                animation: 'suppliesScroll 18s linear infinite',
                width: 'max-content',
              }}
            >
              {[...supplies, ...supplies, ...supplies].map((s, i) => (
                <div
                  key={`${s.name}-${i}`}
                  className="flex flex-col items-center justify-center gap-3 bg-[#111111] border border-white/5 rounded-2xl p-6 h-36 w-40 flex-shrink-0 hover:border-[#C0C0C0]/40 hover:bg-[#222222] transition-all duration-300 cursor-default group"
                >
                  <img
                    src={s.logo}
                    alt={s.name}
                    className="h-16 w-auto max-w-[120px] object-contain brightness-50 group-hover:brightness-100 transition-all duration-300"
                  />
                  <span className="text-white/40 group-hover:text-white/70 text-xs font-medium tracking-widest uppercase transition-colors duration-300">
                    {s.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <style>{`
            @keyframes suppliesScroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(calc(-100% / 3)); }
            }
          `}</style>
        </div>
      </div>
    </section>
  );
}
